import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-only
/**
 * React bindings for `@adminium/i18n` (separate entry so the framework-free
 * core — server emails, jobs, CLI — never pulls React in). Deliberately thin:
 * an `I18nProvider` that re-renders its subtree on `languageChanged` plus
 * hooks bound to the active locale. The `t()` call signature matches the
 * dashboard's key+fallback convention.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, } from 'react';
import { formatFallback } from './fallback.js';
import { getFormatters } from './format/index.js';
import { isRtlLocale, localeFromTag, tagForLocale } from './locales.js';
import { getI18nRevision, subscribeI18nRevision } from './revision.js';
const I18nContext = createContext(null);
export function I18nProvider({ i18n, children }) {
    const [locale, setLocale] = useState(() => localeFromTag(i18n.language ?? 'en-US'));
    useEffect(() => {
        setLocale(localeFromTag(i18n.language ?? 'en-US'));
        const onChanged = (lng) => {
            setLocale(localeFromTag(lng));
        };
        i18n.on('languageChanged', onChanged);
        return () => {
            i18n.off('languageChanged', onChanged);
        };
    }, [i18n]);
    const revision = useSyncExternalStore(subscribeI18nRevision, getI18nRevision, getI18nRevision);
    const value = useMemo(() => ({ i18n, locale, revision }), [i18n, locale, revision]);
    return _jsx(I18nContext.Provider, { value: value, children: children });
}
function useI18nContext(hook) {
    const context = useContext(I18nContext);
    if (context === null)
        throw new Error(`${hook} must be used inside <I18nProvider>`);
    return context;
}
/** The shared i18next instance (advanced use — prefer `useT`). */
export function useI18n() {
    return useI18nContext('useI18n()').i18n;
}
/** Active canonical locale id, live across `changeLanguage`. */
export function useLocale() {
    return useI18nContext('useLocale()').locale;
}
/** `true` iff the active locale reads right-to-left. */
export function useRtl() {
    return isRtlLocale(useI18nContext('useRtl()').locale);
}
/** Translator bound to the provider instance: `t(key, fallback, args?)`. */
export function useT() {
    const { i18n, locale, revision } = useI18nContext('useT()');
    return useCallback((key, fallback, args) => i18n.t(key, { defaultValue: fallback, ...args }), 
    // `locale` and `revision` are dependencies on purpose: a language switch
    // OR a runtime-override change re-binds the callback, so memoized
    // consumers re-render with fresh strings either way.
    [i18n, locale, revision]);
}
/** Provider context, or `null` outside an `<I18nProvider>` (advanced probe). */
export function useMaybeI18n() {
    return useContext(I18nContext);
}
// Shared with the dashboard's module-level `t()` so the two no-instance
// paths cannot drift (see fallback.ts).
/**
 * `useT` for packages whose components must also render OUTSIDE an
 * `<I18nProvider>` (widget chrome in bare tests/Storybook/embeds): inside a
 * provider it is exactly `useT()`; outside, it ICU-formats the English
 * `fallback` argument (en-US rules) instead of throwing, which is the
 * pre-i18n behavior of those components.
 */
export function useMaybeT() {
    const context = useContext(I18nContext);
    const i18n = context?.i18n;
    const locale = context?.locale;
    // Outside a provider there is no context revision to depend on, so subscribe
    // directly — a widget rendered in an embed still has to pick up an edit.
    const revision = useSyncExternalStore(subscribeI18nRevision, getI18nRevision, getI18nRevision);
    return useCallback((key, fallback, args) => i18n === undefined ? formatFallback(fallback, args) : i18n.t(key, { defaultValue: fallback, ...args }), 
    // `locale`/`revision` re-bind on a switch or an override change, as in `useT`.
    [i18n, locale, revision]);
}
/** Locale-bound Intl formatters — `useFmt().number(…)` etc. */
export function useFmt() {
    const { locale } = useI18nContext('useFmt()');
    return getFormatters(tagForLocale(locale));
}
/** Alias per the M8 assignment naming. */
export const useFormatters = useFmt;
//# sourceMappingURL=react.js.map